import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { Grid, Tooltip } from '@enterprise-ui/canvas-ui-react';
import React, { useRef, useState, useEffect, useLayoutEffect, createRef, Fragment } from 'react';
import collapsedChipRenderer from '../renderers/collapsedChipRenderer.js';

var _this = undefined;
var RenderCollapsedChips = function RenderCollapsedChips(_ref) {
  var _ref$value = _ref.value,
    value = _ref$value === void 0 ? [] : _ref$value,
    containerRef = _ref.containerRef,
    renderChip = _ref.renderChip;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    containerWidth = _useState2[0],
    setContainerWidth = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    collapsedMarkerIndex = _useState4[0],
    setCollapsedMarkerIndex = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    start = _useState6[0],
    setStart = _useState6[1];
  var debounce = function debounce(callback, delay) {
    var timeout = 0;
    return function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        return callback.apply(_this, args);
      }, delay);
    };
  };
  useEffect(function () {
    var current = containerRef.current;
    if (current) {
      var onResize = function onResize() {
        return setContainerWidth(function (prev) {
          if (prev < current.offsetWidth) {
            setCollapsedMarkerIndex(function (marker) {
              if (marker !== undefined) return marker + 1;
            });
          }
          return current.offsetWidth;
        });
      };
      var onResizeDebounced = debounce(onResize, 200);
      var observer = new ResizeObserver(onResizeDebounced);
      observer.observe(current);
      return function () {
        observer.disconnect();
      };
    }
  }, [containerRef]);
  useLayoutEffect(function () {
    var width = 0;
    start.forEach(function (v) {
      var _v$ref$current$offset, _v$ref;
      width += (_v$ref$current$offset = (_v$ref = v.ref) === null || _v$ref === void 0 ? void 0 : _v$ref.current.offsetWidth) !== null && _v$ref$current$offset !== void 0 ? _v$ref$current$offset : 0;
    });
    var availableSpace = containerWidth - 128;
    var shouldCollapse = width >= availableSpace;
    if (shouldCollapse) {
      setCollapsedMarkerIndex(start.length == 1 ? 0 : start.length - 1);
    }
  }, [start, containerWidth, collapsedMarkerIndex]);
  useEffect(function () {
    var maxArrayForChipRendering = value.slice(0, 15);
    setStart(maxArrayForChipRendering.slice(0, collapsedMarkerIndex).map(function (item, index) {
      return /*#__PURE__*/React.createElement(Grid.Item, {
        ref: /*#__PURE__*/createRef(),
        className: "C-Autocomplete__ChipContainer",
        key: typeof item.id !== 'undefined' ? item.id : index
      }, renderChip(item));
    }));
  }, [value.toString(), collapsedMarkerIndex, renderChip]);
  var end = value === null || value === void 0 ? void 0 : value.slice(collapsedMarkerIndex);
  var tooltipContent = value.length <= 50 ? end.map(function (i) {
    return i.label;
  }).join(', ') : "+".concat(end.length, " ").concat(end.length === 1 ? 'other' : 'others');
  var collapsedChipLabel = start.length == 0 && end.length > 0 ? "".concat(end.length, " selections") : "+".concat(end.length, " ").concat(end.length === 1 ? 'other' : 'others');
  return /*#__PURE__*/React.createElement(Fragment, null, start, value.length > collapsedMarkerIndex && /*#__PURE__*/React.createElement(Grid.Item, {
    className: "C-Autocomplete__ChipContainer",
    key: "expansion"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    location: "top",
    content: tooltipContent
  }, collapsedChipRenderer({
    id: end.map(function (i) {
      return i.id;
    }),
    label: collapsedChipLabel,
    value: end.join(', ')
  }))));
};
var MultipleInput = function MultipleInput(_ref2, ref) {
  var disabled = _ref2.disabled,
    dataTestId = _ref2['data-testid'],
    id = _ref2.id,
    isExpanded = _ref2.isExpanded,
    maxLength = _ref2.maxLength,
    onChange = _ref2.onChange,
    onFocus = _ref2.onFocus,
    onBlur = _ref2.onBlur,
    placeholder = _ref2.placeholder,
    required = _ref2.required,
    renderChip = _ref2.renderChip,
    selectedOption = _ref2.selectedOption,
    searchText = _ref2.searchText,
    value = _ref2.value,
    grow = _ref2.grow;
  var containerRef = useRef(undefined);
  var innerRef = useRef(null);
  if (ref) {
    if (typeof ref === 'function') {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  }
  return /*#__PURE__*/React.createElement(Grid.Container, {
    ref: containerRef,
    spacing: "none",
    align: "center",
    noWrap: !grow
  }, grow ? value && value.map(function (item, index) {
    return /*#__PURE__*/React.createElement(Grid.Item, {
      className: "C-Autocomplete__ChipContainer",
      key: typeof item.id !== 'undefined' ? item.id : index
    }, renderChip(item));
  }) : /*#__PURE__*/React.createElement(RenderCollapsedChips, {
    containerRef: containerRef,
    renderChip: renderChip,
    value: value
  }), /*#__PURE__*/React.createElement(Grid.Item, {
    xs: true
  }, /*#__PURE__*/React.createElement("input", {
    autoComplete: "off",
    "aria-required": required ? 'true' : 'false',
    "data-test": id,
    "aria-controls": "ariaControls_".concat(id),
    "data-testid": dataTestId ? "".concat(dataTestId, "_input") : null,
    className: "C-Autocomplete__Input",
    id: id,
    ref: innerRef,
    disabled: disabled,
    value: searchText,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    maxLength: maxLength,
    placeholder: placeholder,
    required: required,
    type: "text",
    role: "combobox",
    "aria-expanded": isExpanded,
    "aria-activedescendant": selectedOption ? selectedOption.id : ''
  })));
};
MultipleInput.displayName = 'MultipleInput';
var MultipleInput$1 = /*#__PURE__*/React.forwardRef(MultipleInput);

export { MultipleInput$1 as default };
